/**
 * Approved agreement body content by language.
 * Only include languages that have been legally approved — do not invent translations.
 */

export const confidentialityContentByLang = {
  en: {
    heading: 'PARTNER CONFIDENTIALITY & DATA PROTECTION UNDERTAKING',
    companyLine: 'DhanSthal Financial Services Pvt. Ltd.',
    metaRows: (dateLabel) => [
      ['Undertaking Date', dateLabel],
      ['Given By', 'Partner (As per registration)'],
      ['In Favour Of', 'DhanSthal Financial Services Pvt. Ltd.'],
      ['Nature', 'Unilateral Confidentiality Undertaking'],
    ],
    sections: [
      {
        title: 'Nature of this Undertaking',
        body: "This is a unilateral undertaking given by the Partner in favour of DhanSthal. It does not require DhanSthal to keep the Partner's personal or business information confidential (that protection is separately addressed under the Partner Policy Agreement and DhanSthal's Privacy Policy). This document instead binds the Partner not to disclose or misuse: (a) DhanSthal's confidential business information, and (b) personal data of end-customers/borrowers that the Partner accesses while sourcing or servicing referrals.",
      },
      {
        title: '1. Definition of Protected Information',
        body: '"Protected Information" means:',
        bullets: [
          'Company Confidential Information: commission structures, payout slabs, product pricing, internal approval/underwriting logic, login credentials, API keys, referral/partner codes, dashboard data, sales scripts, marketing materials, and any unreleased products, features, or business strategy shared by DhanSthal.',
          'Customer Data: any personal, financial, or identity information of end-customers/borrowers (e.g., name, contact details, PAN, income, bank details) that the Partner accesses, collects, or receives through the DhanSthal platform while referring or servicing a customer.',
        ],
      },
      {
        title: "2. Partner's Obligations",
        body: 'The Partner agrees to:',
        bullets: [
          'Use Protected Information solely to perform referral and servicing activities under the DhanSthal Partner Program',
          'Not disclose, publish, sell, or share Protected Information with any third party, including competing platforms, other DSAs, or unauthorized personnel',
          'Not use Customer Data for any purpose beyond the specific loan/product application for which it was shared, including for marketing, resale, or onward referral to any other lender or platform',
          'Not retain copies of Customer Data beyond what is necessary to complete the referred transaction',
          'Keep all login credentials, API keys, and partner/referral codes strictly confidential and not share them with any unauthorized person',
        ],
      },
      {
        title: '3. What This Undertaking Does Not Cover',
        body: 'This undertaking does not apply to information that:',
        bullets: [
          'is or becomes publicly available through no fault of the Partner',
          'was already lawfully known to the Partner prior to disclosure by DhanSthal',
          'must be disclosed under a valid order of a court, regulator, or law enforcement authority (in which case the Partner must promptly notify DhanSthal, where legally permitted)',
        ],
      },
      {
        title: '4. Term',
        body: "This Undertaking is effective from the date of signature and remains binding for the duration of the Partner's association with DhanSthal, and for two (2) years following termination or deactivation of the partnership.",
      },
      {
        title: '5. Regulatory Compliance',
        body: 'The Partner shall handle all Customer Data strictly in accordance with the Digital Personal Data Protection Act, 2023, and applicable RBI guidelines on outsourcing and fair practices for DSAs/referral partners.',
      },
      {
        title: '6. Consequences of Breach',
        body: "Any breach of this Undertaking may result in immediate suspension or termination of the Partner's account, forfeiture of pending/unpaid commissions, and DhanSthal reserves the right to pursue legal remedies, including injunctive relief and recovery of damages, against the Partner.",
      },
      {
        title: '7. Governing Law & Jurisdiction',
        body: "This Undertaking shall be governed by the laws of India, with courts at the location of DhanSthal's registered office having exclusive jurisdiction.",
      },
    ],
    footer:
      'By accepting below, the Partner confirms: "I understand that this is a one-way confidentiality undertaking given by me in favour of DhanSthal. I agree not to disclose or misuse DhanSthal\'s confidential business information or any customer data I access while referring business, as detailed above, for the duration of my partnership and for two (2) years thereafter."',
  },
};

export const amlPolicyContentByLang = {
  en: {
    heading: 'ANTI-MONEY LAUNDERING (AML) POLICY AGREEMENT',
    companyLine: 'DhanSthal Financial Services Pvt. Ltd.',
    metaRows: (dateLabel) => [
      ['Agreement Date', dateLabel],
      ['Company', 'DhanSthal Financial Services Pvt. Ltd.'],
      ['Partner', 'As per registration'],
      ['Regulatory Framework', 'PMLA 2002 / RBI AML Guidelines'],
    ],
    sections: [
      {
        title: '1. Purpose & Regulatory Basis',
        body: 'This Agreement sets out the obligations of the Partner with respect to Anti-Money Laundering (AML) and Counter-Financing of Terrorism (CFT) compliance. The Partner acknowledges that DhanSthal Financial Services Pvt. Ltd. is required to comply with the Prevention of Money Laundering Act, 2002 (PMLA), the rules made thereunder, and guidelines issued by the Reserve Bank of India (RBI) from time to time. The Partner agrees to support the Company in meeting these obligations.',
      },
      {
        title: '2. Know Your Customer (KYC) Obligations',
        bullets: [
          'Collect and verify customer identity and address documents as required by the Company before submitting any loan application.',
          'Never submit applications on behalf of customers whose identity cannot be verified or who are unwilling to provide required KYC documents.',
          'Ensure that all KYC documents collected are genuine and not tampered with to the best of your knowledge.',
          'Promptly report to the Company if a customer is unable or unwilling to provide valid KYC documents.',
          'Never assist a customer in evading KYC requirements through any means.',
        ],
      },
      {
        title: '3. Suspicious Transaction Reporting',
        body: "The Partner agrees to immediately report to the Company any customer, transaction, or circumstance that appears unusual or suspicious, including: customers who are reluctant to provide information, transactions that appear inconsistent with a customer's stated income or purpose, requests to structure transactions to avoid documentation thresholds, or any other activity that may indicate money laundering or fraud.",
      },
      {
        title: '4. Prohibited Activities',
        bullets: [
          'Do not facilitate or assist in any transaction that you know or suspect to involve proceeds of crime.',
          'Do not accept cash or undisclosed payments from customers on behalf of the Company.',
          'Do not submit applications for customers who you know to be involved in illegal activities.',
          'Do not assist customers in misrepresenting income, employment, or purpose of loan.',
          'Do not create or participate in any scheme designed to circumvent AML controls.',
        ],
      },
      {
        title: '5. Customer Due Diligence (CDD)',
        body: "The Partner shall perform basic customer due diligence by understanding the purpose of the loan, verifying the customer's stated source of income, and assessing whether the loan request appears consistent with the customer's financial profile. The Partner shall flag any discrepancies to the Company before submitting the application.",
      },
      {
        title: '6. Record Keeping',
        body: 'The Partner shall maintain records of all customer interactions, documents collected, and referrals made to the Company for a minimum period of five (5) years from the date of the transaction or the termination of the business relationship, whichever is later, and shall make such records available to the Company or regulatory authorities upon request.',
      },
      {
        title: '7. Training & Awareness',
        body: 'The Partner acknowledges having sufficient awareness of AML/CFT risks associated with the financial services sector and agrees to stay updated on relevant regulatory developments. The Partner shall not engage any sub-agents or representatives without ensuring they are also aware of and bound by these AML obligations.',
      },
      {
        title: '8. Consequences of Non-Compliance',
        body: 'Any breach of this AML Policy Agreement may result in immediate termination of the partnership, forfeiture of all pending commissions, reporting to the Financial Intelligence Unit – India (FIU-IND) or other regulatory authorities, and potential civil or criminal liability under the PMLA and other applicable laws.',
      },
    ],
    footer:
      'By accepting below, the Partner confirms they have read, understood, and agree to comply with this Anti-Money Laundering Policy Agreement.',
  },
};

export function getAgreementBodyContent(agreementKey, languageCode) {
  const map =
    agreementKey === 'aml_policy' ? amlPolicyContentByLang : confidentialityContentByLang;
  return map[languageCode] || null;
}

export function formatAgreementDateLabel(date = new Date()) {
  return date.toLocaleDateString('en-IN', { day: '2-digit', month: 'long', year: 'numeric' });
}
